import { useEffect, useId, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaPlay } from 'react-icons/fa6';
import './assets/videoPlayer.css';
import VideoPlayerControls from './components/VideoPlayerControls/VideoPlayerControls';
import useVideoPlayerController from './hooks/useVideoPlayerController';

const STABILIZING_OVERLAY_MIN_MS = 1200;

function formatRuntimeCutoffLabel(runtimeCutoffMs) {
  if (!Number.isFinite(runtimeCutoffMs) || runtimeCutoffMs <= 0) return 'the runtime limit';

  const totalSeconds = Math.round(runtimeCutoffMs / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds} second${totalSeconds === 1 ? '' : 's'}`;
  }

  const totalMinutes = totalSeconds / 60;
  if (Number.isInteger(totalMinutes)) {
    return `${totalMinutes} minute${totalMinutes === 1 ? '' : 's'}`;
  }

  return `${totalSeconds} seconds`;
}

/**
 * A resilient HLS Video Stream Player.
 * Stream lifecycle/recovery lives in the controller hook; this component stays focused on layout.
 */
export default function VideoPlayer({
  ariaLabel = 'Camera feed',
  className,
  runtimeCutoffMs,
  verticalShrink,
  src,
  autoplay = true,
  setActiveAudioIndex = () => {},
  activeAudioIndex,
  selfAudioIndex,
  style = {},
  widgetRuntime,
}) {
  const controller = useVideoPlayerController({
    activeAudioIndex,
    autoplay,
    runtimeCutoffMs,
    selfAudioIndex,
    setActiveAudioIndex,
    src,
    widgetRuntime,
  });
  const playerId = useId();
  const playerTitleId = `${playerId}-title`;
  const playerDescriptionId = `${playerId}-description`;
  const playerStatusId = `${playerId}-status`;
  const videoId = `${playerId}-media`;
  const pausedHeadingId = `${playerId}-paused-heading`;
  const pausedDescriptionId = `${playerId}-paused-description`;
  const playerLabel = ariaLabel || 'Camera feed';
  const runtimeCutoffLabel = formatRuntimeCutoffLabel(runtimeCutoffMs);
  const [showStabilizingOverlay, setShowStabilizingOverlay] = useState(false);
  const continueButtonRef = useRef(null);
  const overlayShownAtRef = useRef(0);
  const playerStatusText = (() => {
    if (controller.watchTimeLimitReached) {
      return `Feed paused. This video stream ended after ${runtimeCutoffLabel}.`;
    }

    if (controller.runtimeGateWarningActive) {
      return 'Runtime limit warning. Select Keep Watching to continue the feed.';
    }

    if (showStabilizingOverlay) {
      return 'Stabilizing video stream.';
    }

    if (controller.hasActiveStream && controller.paused) {
      return 'Video stream paused.';
    }

    if (controller.hasActiveStream) {
      return 'Video stream playing.';
    }

    return 'Video stream not started.';
  })();

  useEffect(() => {
    let hideTimer;

    if (controller.loading && !controller.watchTimeLimitReached) {
      overlayShownAtRef.current = Date.now();
      setShowStabilizingOverlay(true);
      return undefined;
    }

    const remainingMs = Math.max(
      0,
      STABILIZING_OVERLAY_MIN_MS - (Date.now() - overlayShownAtRef.current),
    );

    hideTimer = setTimeout(() => {
      setShowStabilizingOverlay(false);
    }, remainingMs);

    return () => clearTimeout(hideTimer);
  }, [controller.loading, controller.watchTimeLimitReached]);

  useEffect(() => {
    if (controller.watchTimeLimitReached) {
      continueButtonRef.current?.focus();
    }
  }, [controller.watchTimeLimitReached]);

  return (
    <section
      ref={controller.fullscreenRef}
      className={`vms-video-player position-relative d-flex flex-column flex-fill w-100 h-100 overflow-hidden bg-black rounded-3 ${className || ''}`}
      style={style}
      data-altshrink={verticalShrink || undefined}
      aria-labelledby={playerTitleId}
      aria-describedby={`${playerDescriptionId} ${playerStatusId}`}
    >
      <h2 id={playerTitleId} className="visually-hidden">
        {playerLabel}
      </h2>
      <p id={playerDescriptionId} className="visually-hidden">
        Live video stream. Playback controls follow the video when a stream is active.
      </p>
      <p
        id={playerStatusId}
        className="visually-hidden"
        aria-live={controller.watchTimeLimitReached ? 'assertive' : 'polite'}
        aria-atomic="true"
      >
        {playerStatusText}
      </p>

      <div className="vms-video-viewport position-relative flex-grow-1 min-h-0 w-100 overflow-hidden">
        {controller.runtimeGateWarningActive && !controller.watchTimeLimitReached && (
          <Button
            variant="dark"
            className="vms-video-keep-watching vms-video-floating-action position-absolute d-inline-flex align-items-center gap-2 fw-semibold text-nowrap"
            onClick={controller.acceptRuntimeGate}
            aria-label={`Keep watching and reset the timeout. ${controller.runtimeGateWarningSecondsRemaining} seconds remaining.`}
            aria-describedby={playerStatusId}
          >
            <span>Keep Watching</span>
            <span className="vms-video-keep-watching-badge">
              {controller.runtimeGateWarningSecondsRemaining}s
            </span>
          </Button>
        )}

        {controller.watchTimeLimitReached && (
          <div
            className="vms-video-state-overlay vms-video-overlay position-absolute-fill z-2 d-flex flex-column justify-content-center align-items-center gap-3 px-4 text-center text-white"
            role="group"
            aria-labelledby={pausedHeadingId}
            aria-describedby={pausedDescriptionId}
          >
            <div>
              <div id={pausedHeadingId} className="small fw-semibold text-uppercase mb-2">
                Feed Paused
              </div>
              <div id={pausedDescriptionId} className="fs-5 fw-semibold">
                This video stream ended after {runtimeCutoffLabel}.
              </div>
              <div className="small mt-2">
                Select continue when you are ready to restart the feed.
              </div>
            </div>
            <Button
              ref={continueButtonRef}
              variant="light"
              className="vms-video-primary-action fw-semibold px-4 py-2"
              onClick={controller.acceptRuntimeGate}
              aria-describedby={pausedDescriptionId}
            >
              Continue Watching
            </Button>
          </div>
        )}

        {!controller.watchTimeLimitReached &&
          !controller.hasActiveStream &&
          !controller.loading && (
            <Button
              variant="link"
              className="vms-video-start-overlay vms-video-overlay position-absolute-fill z-1 d-flex justify-content-center align-items-center rounded-0 border-0 text-white text-decoration-none"
              onClick={() => controller.play()}
              aria-controls={videoId}
              aria-describedby={playerStatusId}
              aria-label="Start video stream"
            >
              <span className="vms-video-start-button-surface d-inline-flex align-items-center justify-content-center">
                <FaPlay
                  className="vms-video-start-icon"
                  size={42}
                  aria-hidden="true"
                  focusable="false"
                />
              </span>
            </Button>
          )}

        {showStabilizingOverlay && !controller.watchTimeLimitReached && (
          <>
            <div className="vms-video-dim-overlay position-absolute-fill z-1" aria-hidden="true" />
            <div
              className="vms-video-state-overlay vms-video-overlay position-absolute-fill z-2 d-flex flex-column justify-content-center align-items-center gap-3 text-white"
              role="status"
              aria-live="polite"
              aria-label="Stabilizing video stream"
            >
              <Spinner animation="border" aria-hidden="true" />
              <span className="small fw-semibold text-uppercase">Stabilizing Stream...</span>
            </div>
          </>
        )}

        <video
          id={videoId}
          key={controller.mediaElementKey}
          className="w-100 h-100"
          style={{ objectFit: controller.fitMode }}
          ref={controller.videoRef}
          aria-label={`${playerLabel} video stream`}
          aria-describedby={playerStatusId}
          muted={controller.muted}
          playsInline
          {...controller.videoEventHandlers}
        />
      </div>

      {controller.hasActiveStream && (
        <VideoPlayerControls
          bufferedProgress={controller.bufferedProgress}
          bufferedSecondsAhead={controller.bufferedSecondsAhead}
          controlsLabel={`${playerLabel} playback controls`}
          controlsDisabled={controller.controlsDisabled}
          fitLabel={controller.fitLabel}
          fitDescription={controller.fitDescription}
          loading={controller.loading}
          mediaId={videoId}
          muted={controller.muted}
          onCycleFitMode={controller.cycleFitMode}
          onHandleVolumeSlider={controller.handleVolumeSlider}
          onPause={controller.pause}
          onRestart={controller.restart}
          onResetAndRestart={controller.resetAndRestart}
          onResume={controller.resume}
          onSeek={controller.seek}
          onStop={controller.stop}
          onToggleMute={controller.toggleMute}
          paused={controller.paused}
          progress={controller.progress}
          volume={controller.volume}
          fullscreenLabel={controller.fullscreenLabel}
          isFullscreen={controller.isFullscreen}
          onToggleFullscreen={controller.toggleFullscreen}
          fullscreenAvailable={controller.fullscreenAvailable}
        />
      )}
    </section>
  );
}
